import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { getImage, isReportXpert, scrollToQuestion } from '@aytm/app/stats/lib/stat-page'

import { calcCompareItemsProps } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/compare-items-utils'
import {
  calcConceptAttributes,
  calcConceptItems,
  getMainAttributePosition
} from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/concept-items'
import { resolveFormat } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/report-params'
import { DEFAULT_SPLIT_BY } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/split-groups'
import useCompareItemsState from '@aytm/app/stats/page/question-views/info-view/components/compare-items/states/useCompareItemsState'
import useSplitGroups from '@aytm/app/stats/page/question-views/info-view/components/compare-items/states/useSplitGroups'

// components
import ImagePopup from '@aytm/lib/rfd-ui/components/basic/image-popup'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import { default as CompareItems } from '@aytm/app/stats/components/data-views/compare-items/components/CompareItems'

// styles
import style from '@aytm/app/stats/page/question-views/info-view/components/compare-items/styles/Block.module.scss'

// Info-view compare-items block: derives the table data and owns the block state, then renders
// the CompareItems DataView. The page view keeps the survey-coupled work (report-param writing,
// export, concept questions) and pulls whatever it needs from the api handed up on mount.
const Block = ({
  id,
  params,
  title,
  fullScreenTitle,
  itemName,
  block,
  format: formatProp,
  batches,
  answer,
  template,
  questionId,
  questionKey,
  link,
  splitBy: splitByProp,
  percentDecimals,
  showExportButton,
  onReady,
  onStateChange,
  onExport
}) => {
  const format = useMemo(() => resolveFormat(formatProp, batches), [formatProp, batches])
  const attributes = useMemo(() => calcConceptAttributes(batches), [batches])
  const items = useMemo(
    () => calcConceptItems({ batches, answer, attributes, splitBy: splitByProp }),
    [batches, answer, attributes, splitByProp]
  )
  const mainAttributePosition = getMainAttributePosition(attributes)

  const {
    concepts,
    preset,
    attribute,
    reverse,
    analysisMode,
    showImages,
    fullTexts,
    splitBy,
    api,
    onAnalysisModeUpdated,
    onSelectedItemsUpdated,
    onShowImagesUpdated,
    onFullTextsUpdated,
    onChangeSplitBy
  } = useCompareItemsState({
    params,
    format,
    block,
    items,
    attributes,
    mainAttributePosition,
    splitBy: splitByProp,
    template,
    batches,
    answer,
    questionId,
    questionKey,
    id,
    link,
    percentDecimals,
    onReady,
    onStateChange
  })

  const splitByEnabled = false !== format?.enableSplitBy && !isReportXpert()
  const splitGroups = useSplitGroups(splitByEnabled)
  // A picked split waits for an answer that carries it; drawn earlier, its cells are empty until the reload's
  // placeholder replaces the table.
  const carried = split =>
    null == split?.ref || batches?.some(batch => Object.hasOwn(answer?.[`b${batch.position}`] || {}, split.ref))
  const drawnSplitBy = (splitByEnabled && [splitBy, splitByProp].find(carried)) || DEFAULT_SPLIT_BY

  const scrollToDetails = false !== format?.showScrollToDetails

  const [popupImage, setPopupImage] = useState(null)

  const derived = calcCompareItemsProps({
    format,
    items,
    attributes,
    concepts,
    preset,
    attribute,
    reverse,
    analysisMode,
    showImages,
    fullTexts
  })

  return (
    <BlockWrapper>
      <div className={style.component}>
        <CompareItems
          id={id}
          title={title}
          fullScreenTitle={fullScreenTitle}
          itemName={itemName}
          items={items}
          attributes={attributes}
          {...derived}
          percentDecimals={percentDecimals}
          splitBy={drawnSplitBy}
          splitGroups={splitGroups}
          showExportButton={showExportButton}
          onAnalysisModeUpdated={onAnalysisModeUpdated}
          onSelectedItemsUpdated={onSelectedItemsUpdated}
          onShowImagesUpdated={onShowImagesUpdated}
          onFullTextsUpdated={onFullTextsUpdated}
          onClickItem={
            (scrollToDetails &&
              ((itemIndex, item, clickedItems) =>
                void scrollToQuestion(`${questionId}_${clickedItems[itemIndex].position}`))) ||
            null
          }
          onClickImage={(itemIndex, item, clickedItems) => void setPopupImage(getImage(clickedItems[itemIndex].image))}
          onExportData={callback => {
            const { fileType, exportType, exportOptions } = api.getDownloadParams()
            onExport?.(fileType, exportType, exportOptions, callback)
          }}
          onChangeSplitBy={(splitByEnabled && onChangeSplitBy) || null}
        />
      </div>
      <ImagePopup
        src={popupImage?.original?.url}
        show={null != popupImage}
        scrolling={false}
        closeOnClick
        onClose={() => void setPopupImage(null)}
      />
    </BlockWrapper>
  )
}

Block.displayName = 'CompareItemsBlock'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  params: PropTypes.shape({ cts: PropTypes.object }),
  title: PropTypes.string,
  fullScreenTitle: PropTypes.string,
  itemName: PropTypes.string,
  block: PropTypes.object,
  format: PropTypes.object,
  batches: PropTypes.array,
  answer: answerPropType,
  template: PropTypes.object,
  questionId: PropTypes.string,
  questionKey: PropTypes.string,
  link: PropTypes.string,
  splitBy: PropTypes.object,
  percentDecimals: PropTypes.number,
  showExportButton: PropTypes.bool,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onExport: PropTypes.func
}

export default Block
